export const privacyPolicyEn = `
**Policy Number:** 1.11.3.1

**Responsible Unit:** Information Technology Services 

**Effective Date:** May 13, 2024

**Revision History:** Originally effective March 16, 2017; Revised August 1, 2018

**Next Review Date:** May 12, 2025

## **1\. Purpose and Scope:**

1.1. West Virginia University, West Virginia University Institute of Technology, and Potomac State College of West Virginia University (“University”) respects the privacy of individuals and seeks to limit the collection, access, use, disclosure, and storage of Personally Identifiable Information (“PII”). 

1.2. The purpose of this Policy is to: 

1.2.1. Identify the PII that the University collects; 

1.2.2. Identify how the University collects PII; and,

1.2.3. Explain how the University uses and protects the PII it collects.

1.3. This Policy applies to all University staff, faculty, students, patients, Third-Party Services who are doing work on behalf of the University, and visitors to campus.

## **2\. Information Privacy at the University:**

2.1. The University seeks to limit the collection of PII to that which reasonably serves its academic, research, service, outreach, and administrative functions. 

2.2. The University collects PII through websites and social media pages, that does not reveal specific identities, including but not limited to: geographic location, device, internet browser, and operating system.

2.3. The University will not collect PII, such as name, address, Social Security number, or financial information, without an individual’s express consent. 

2.4. The University collects PII through various methods, including, but not limited to:

2.4.1. Applications made available for use on or through computers and mobile devices; 

2.4.2. HTML-formatted email messages sent by the University; 

 

2.4.3. Patient care, health treatment, and research;

2.4.4. Offline activities (e.g., campus visit, attend seminar, place a request over the phone); and,

2.4.5. Other sources such as publicly available databases or joint marketing partners who share information with the University.

2.5.  The University uses PII for legitimate business and operational purposes, including, but not limited to:

2.5.1. Providing services, complete transactions, fulfill requests, and send administrative information;

2.5.2. Providing newsletters and/or other promotional materials; and,

2.5.3. Analyzing, aggregating, and anonymizing data for reporting.

2.6. The University will never sell or market PII to third-parties or affiliates.

2.7. When necessary to provide a service or to support its mission, the University may disclose PII: 

2.7.1. To affiliates or Third-Party Services with whom it has a contractual agreement;

2.7.2. To facilitate business operations within the University; 

2.7.3. To protect the safety and well-being of individuals or the community; and,

2.7.4. As permitted or required by law.

2.8. When the distribution of PII is necessary to third-parties, the distribution will be subject to reasonable terms and conditions that will, among other things: 

2.8.1. Limit the access to and use of the data to only authorized individuals for legitimate business purposes; 

2.8.2. Never be for marketing or promotional purposes that are not on behalf of the University; 

2.8.3. Never share the individual's original consent; 

2.8.4. Ensure the data will remain secure; and, 

2.8.5. Ensure the data will be returned or destroyed when its purpose for distribution ends. 

2.9. The University will implement safeguards to secure the integrity and confidentiality of PII collected, including, but not limited to:

2.9.1. Publishing associated standards and procedures identifying the minimum requirements to safeguard PII; and,

2.9.2. Educating and providing privacy awareness to its workforce regarding collection and safeguarding of PII.

2.10. Unauthorized access or disclosure of PII must be reported to Information Technology Services by email at defendyourdata@mail.wvu.edu or by [submitting an Incident Report Form](https://wvu.qualtrics.com/jfe/form/SV_bHtj4r0l3sXPbts) within 24 hours of the event as per the Computer Security Incident Response Policy.

2.11. The University may disclose PII deemed Directory Information at its discretion. 

2.12. The University will retain PII according to the Record Retention Policy and Schedule. All PII will be destroyed in accordance with best practices and as required by applicable laws at the time of destruction.

2.13. The University will ensure that data processed or stored in University systems hosted by Third-Party Services are compliant with this Policy and relevant laws and regulations; however, the University is not responsible for the information collection, use, disclosure, or security policies or practices of Third-Party Services. 

2.14. All University units are responsible for establishing and making available for view appropriate privacy notices related to their collection, use, distribution, and destruction of PII.

2.15. Those University units designated as a University Health Care Component, pursuant to the HIPAA Hybrid Entity Designation Policy, may only use and disclose protected health information ("PHI") to a University non-health care component to the same extent, and in the same manner, as it is permitted to use or disclose PHI to individuals or entities that are legally separate from the University.

2.16. The University will never take retaliatory action against a student, patient, physician, employee, or any other person for exercising their rights established under this Policy, including submitting a complaint or reporting a violation. 

2.17. Any attempt to retaliate against a person for reporting a privacy violation may itself be considered a violation of this Policy and may result in sanctions.

2.18. The University reserves the right to update this Policy at any time in the future and to make the revised change notice effective for personal data already collected or will receive in the future.

## **3\. Individual Rights:**

3.1. Individuals have the right to:

3.1.1. Expect that their PII collected by the University will remain private and secure; 

3.1.2. Access and review their PII to confirm accuracy and completeness; 

3.1.3. Be notified when PII has been disclosed or accessed by an unauthorized person;

3.1.4. Request to amend or delete their PII, if appropriate; 

3.1.5. Opt out of receiving electronic communications; and,

3.1.6. Request the University withhold disclosure of their PII.

3.2. Individuals are advised to be discreet and cautious in their use of University Technology Resources and are obliged to abide by the Acceptable Use of Data and Technology Resources Policy.

3.3. Individuals who disclose other people’s PII to the University or our Third-Party Services represent that they have the authority to do so and permit us to use the information in accordance with this Policy.

3.4. Misuse of PII is a violation of this Policy. PII collected by the University must not be misused. Misuse includes, but is not limited to, the following:

3.4.1. Seeking or soliciting Social Security numbers via email or phone;

3.4.2. Sending or knowingly accepting credit card information by email;

3.4.3. Storing credit card information on University-owned computers;

3.4.4. Unnecessarily accessing PII;

3.4.5. Using PII of another person for personal gain;

3.4.6. Not reporting when PII is stored inappropriately or disclosed;

3.4.7. Unilaterally updating PII without the individual’s request or consent; 

3.4.8. Downloading and sharing mobile information used to text an individual without first receiving the individual's consent to do so; and,

3.4.9. Compiling copies or duplicates of PII without the individual’s approval, except for back up or disaster recovery purposes.

## **4\. Definitions:**

3.1. “Confidentiality” means a set of rules that limit access or place restrictions on certain types of information to protect personal privacy and proprietary information.

3.2. “Directory Information” means the University considers the following information it collects Directory Information: name, official address, telephone number, place of birth, age of student, names and addresses of parents, major and minor fields of study, class status (e.g., freshman), enrollment status (e.g., full-time, part-time), dates of attendance, previous educational institution(s) attended, degree(s) and date(s) conferred including anticipated graduation dates, awards, honors, participation in officially recognized sports and activities, physical factors of athletes, and duties, responsibilities, and dates of service of Graduate Assistant, Student Workers, Interns, or Student Volunteers.

3.3. “Integrity” means the overall completeness, accuracy, and consistency of the data.

3.4. “Other Personal Information" means data that does not identify a specific identity or relate to an identifiable individual unless combined with other personal data such as physical location, IP address, browser/device information, app usage data, demographic information, appearance, religion, political opinions, and behavior.

3.5. “Personally Identifiable Information (PII)” means any information that can be used to identify an individual either alone or when combined with other personal information. PII the University considers Sensitive is identified within the Data Classification Policy and includes Social Security numbers, driver’s license numbers, passport or visa numbers, biometric images, and University authentication credentials. Other personal information means data that does not identify a specific identity or relate to an identifiable individual unless combined with other personal data such as physical location, IP address, browser/device information, app usage data, demographic information, appearance, religion, political opinions, and behavior.

3.6. “Protected Health Information (PHI)” means individually identifiable health information held or transmitted by a named UHCC in any form or media, whether electronic, paper, or oral. Individually identifiable health information includes names, geographic subdivisions smaller than a State, telephone numbers, Social Security numbers, medical record numbers, biometric identifiers, and more. For further explanation, see the [Protected Health Information Privacy Policy](https://it.wvu.edu/policies-and-procedures/privacy/protected-health-information-privacy-policy).

3.7. “Third-Party Services” means an individual or an entity that has a contract, license, or other arrangement with the University to provide a technology service.

3.8. “University Information Systems” means the hardware, software, and related technology that supports the academic, administrative, learning and research operations at the University.

## **5\. Enforcement and Interpretation:**

4.1. Any employee who violates this Policy will be subject to appropriate disciplinary action.

4.2. Any student who violates this Policy will be subject to appropriate disciplinary action in accordance with the Student Code of Conduct.

4.3. Any Individual affiliate with the University who violates this Policy will be subject to appropriate corrective action, including, but not limited to, termination of the individual’s relationship with the University.

4.4. The University’s Chief Information Officer or their designee, supported by the Chief Information Privacy Officer and the Chief Information Security Officer, will coordinate with appropriate University entities on the implementation and enforcement of this Policy.

 

4.5. Responsibility for interpretation of this Policy rests with the Chief Information Officer or their designee.

## **6\. Authority and References:**

5.1. [BOG Rule 1.11 — Information Technology Resources and Governance](https://policies.wvu.edu/finalized-bog-rules/bog-governance-rule-1-11-information-technology-resources-and-governance)

5.2. All other University policies are also applicable to the electronic environment. Relevant institutional policies include, but are not limited to:

5.2.1. [Acceptable Use of Data and Technology Resources Policy](https://wvu-privacy.my.onetrust.com/policies/22f94806-5b2c-4a13-9491-08af2ebcebea?context=eyJkc1BvcnRhbElkIjoiOWI2YWZiZTAtYTdhNS00MmM5LWE3ZjEtZjAxNDc2OGYzOWM1In0%3D)

5.2.2. [HIPAA Hybrid Entity Designation Policy](https://it.wvu.edu/policies-and-procedures/privacy/hipaa-hybrid-entity-designation-policy)

5.2.3. [Protected Health Information Privacy Policy](https://it.wvu.edu/policies-and-procedures/privacy/protected-health-information-privacy-policy)

5.2.4. [Identity Theft Detection and Prevention Policy](https://it.wvu.edu/policies-and-procedures/privacy/identity-theft-detection-prevention-policy)

5.2.5. [Information Security Policy](https://it.wvu.edu/policies-and-procedures/security/information-security-policy)

5.2.6. [WVU Record Retention Policy & Schedule](https://generalcounsel.wvu.edu/files/d/c21641ab-a271-4985-b245-4ce13478f398/wvu-policy-g-1-record-retention-policy-1-1-18-w0067584xd4daa.pdf)

5.2.8. [WVU FERPA Policies](http://ferpa.wvu.edu/)

5.2.9. [West Virginia Freedom of Information Act](http://www.wvlegislature.gov/wvcode/code.cfm?chap=29b&art=1)

`;
